import React, { useState, useEffect, useCallback } from 'react';
import { SearchBar } from './SearchBar';
import { DemoCard } from './DemoCard';
import { VideoModal } from './VideoModal';

/** Demo shape from the BFF API */
interface DemoItem {
  id: string;
  title: string;
  description: string;
  projectName: string;
  category: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  createdBy: string;
  webUrl: string;
  hasVideo: boolean;
  videoFileId: string | null;
  videoFileName: string | null;
  videoMimeType: string | null;
  videoSize: number;
  artifacts: Array<{
    id: string;
    name: string;
    mimeType: string;
    size: number;
    webUrl: string;
    downloadUrl?: string;
  }>;
  hasLiveDemo: boolean;
  liveDemoUrl: string | null;
}

interface DemosApiResponse {
  demos: DemoItem[];
  total: number;
  categories: string[];
  nextCursor: string | null;
}

/**
 * DemoCatalog displays the list of demos with search and category filter.
 */
export const DemoCatalog: React.FC = () => {
  const [demos, setDemos] = useState<DemoItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [selectedDemo, setSelectedDemo] = useState<DemoItem | null>(null);

  const fetchDemos = useCallback(async (search: string, category: string) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (search.length > 0) params.set('search', search);
      if (category.length > 0) params.set('category', category);

      const queryString = params.toString();
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api';
      const url = `${baseUrl}/demos${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch demos: ${response.statusText}`);
      }

      const data: DemosApiResponse = await response.json();
      setDemos(data.demos);
      setTotal(data.total);
      if (data.categories) setCategories(data.categories);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDemos(searchTerm, activeCategory);
  }, [searchTerm, activeCategory, fetchDemos]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleCategoryClick = (cat: string) => {
    setActiveCategory(activeCategory === cat ? '' : cat);
  };

  return (
    <div>
      <SearchBar onSearch={handleSearch} />

      {/* Category Filter Tabs */}
      {categories.length > 0 && (
        <div className="category-tabs" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setActiveCategory('')}
            className={`category-tab ${activeCategory === '' ? 'category-tab--active' : ''}`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => handleCategoryClick(cat)}
              className={`category-tab ${activeCategory === cat ? 'category-tab--active' : ''}`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div className="loading-spinner" aria-live="polite">
          <div className="spinner" />
          <p className="loading-text">Loading demos from SharePoint...</p>
        </div>
      )}

      {error && (
        <div className="error-container" role="alert">
          <span className="error-icon">⚠️</span>
          <div className="error-content">
            <p className="error-message">{error}</p>
            <div className="error-action">
              <button className="btn-primary" onClick={() => fetchDemos(searchTerm, activeCategory)}>
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && demos.length === 0 && searchTerm.length > 0 && (
        <div className="state-container" aria-live="polite">
          <div className="state-icon">🔍</div>
          <h2 className="state-title">No results found</h2>
          <p className="state-desc">
            No demos match &ldquo;{searchTerm}&rdquo;. Try a different search term.
          </p>
          <button className="btn-secondary" onClick={handleClearSearch}>
            Clear search
          </button>
        </div>
      )}

      {!loading && !error && demos.length === 0 && searchTerm.length === 0 && (
        <div className="state-container" aria-live="polite">
          <div className="state-icon">📦</div>
          <h2 className="state-title">No demos yet</h2>
          <p className="state-desc">
            No demos found in this category. Try selecting a different category.
          </p>
        </div>
      )}

      {!loading && !error && demos.length > 0 && (
        <>
          <p className="result-count">
            Showing {demos.length} demo{demos.length !== 1 ? 's' : ''}
            {activeCategory ? ` in ${activeCategory}` : ''}
          </p>
          <div className="demo-grid" role="list">
            {demos.map((demo) => (
              <div key={demo.id} onClick={() => setSelectedDemo(demo)}>
                <DemoCard demo={demo as any} />
              </div>
            ))}
          </div>
        </>
      )}

      {/* Video Modal */}
      {selectedDemo && (
        <VideoModal
          demo={selectedDemo}
          onClose={() => setSelectedDemo(null)}
        />
      )}
    </div>
  );
};
